import { CalendlyExpert } from "../api-objects/CalendlyExpert";
import { Patient } from "../api-objects/Patient";
import { BASE_URL, requestOptionsGET } from "../Constants";
import { ExpertRole } from "../Enums";
import { ErrorWithMessageCallback, GenericSuccessCallback, GetMedicalUserSuccessCallback } from "./callback-helpers";

export async function getMedicalUserInfoApi(
    idToken: string,
    userId: string,
    onSuccess: GetMedicalUserSuccessCallback,
    onError: ErrorWithMessageCallback,
    handleSessionExpiry?: () => void,
) {
    try {
        const response = await fetch(`${BASE_URL}/api/medical-user/${userId}`, requestOptionsGET(idToken))
        if (response.ok) {
            const user: CalendlyExpert = await response.json()
            onSuccess(user)
        } else if (response.status === 401 && handleSessionExpiry) {
            handleSessionExpiry()
        } else {
            onError("Errore")
        }
    } catch (error) {
        console.error("Get medical user info failed:", error);
        return { success: false, error: "An unexpected error occurred" };
    }
}

export async function getPatientsApi(
    idToken: string,
    userId: string,
    role: string,
    handleSessionExpiry?: () => void
): Promise<Patient[] | null> {
    try {
        const response = await fetch(
            `${BASE_URL}/api/medical-user/${userId}/patients/search?role=${role}`,
            requestOptionsGET(idToken)
        );
        if (!response.ok) {
            if (response.status === 401 && handleSessionExpiry) {
                handleSessionExpiry(); // Handle session expiry if needed
            }
            throw new Error(`Failed to fetch patients: ${response.statusText}`);
        }
        return await response.json();
    } catch (error) {
        console.error("Get patients failed:", error);
        return null;
    }
}

export async function syncCalendlyApi(
    idToken: string,
    userId: string,
    onSuccess: GenericSuccessCallback,
    onError: ErrorWithMessageCallback,
) {
    const responseUserinfo = await fetch(`${BASE_URL}/api/medical-user/sync-calendly/${userId}`, {
        method: 'PUT',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': idToken,
        },
    })
    if (responseUserinfo.ok) {
        const success = await responseUserinfo.json()
        onSuccess(success)
    } else {
        onError("Errore nel sincronizzare Calendly")
    }
}

export async function updateMedicalUserApi(
    idToken: string,
    userId: string,
    name: string,
    family_name: string,
    phone_number: string,
    titolo: string,
    professional_order: string,
    professional_order_number: string,
    professional_order_state: string,
    tags: string[],
    bio: string,
    onSuccess: GetMedicalUserSuccessCallback,
    onError: ErrorWithMessageCallback,
) {
    const data = {
        name,
        family_name,
        phone_number,
        sex: titolo === "Dott." ? "M" : "F",
        professional_order,
        professional_order_number,
        professional_order_state,
        bio,
    };

    const payload = {
        data,
        tags,
    };

    const responseUserinfo = await fetch(`${BASE_URL}/api/medical-user/${userId}`, {
        method: 'PUT',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': idToken,
        },
        body: JSON.stringify(payload)
    })
    if (responseUserinfo.ok) {
        const user: CalendlyExpert = await responseUserinfo.json()
        onSuccess(user)
    } else {
        onError("Errore nell'update")
    }
}

export async function getMedicaluserByRole(
    idToken: string,
    role: ExpertRole,
): Promise<CalendlyExpert[] | null> {
    try {
        const response = await fetch(`${BASE_URL}/api/medical-user/role/${role}`, requestOptionsGET(idToken))
        if (response.ok) {
            const experts: CalendlyExpert[] = await response.json()
            return experts
        } else if (response.status === 401) {
            throw new Error("Session expired");
        } else if (response.status === 404) {
            console.warn(`Experts not found with role: ${role}`)
            return null;
        } else {
            const errorMessage = response.statusText || "Failed to fetch experts";
            throw new Error(errorMessage);
        }
    } catch (error) {
        console.error("Get experts failed:", error);
        if (error instanceof Error) {
            throw error;
        } else {
            throw new Error("An unexpected error occurred");
        }
    }
}

export async function removePatientFromMedicalUserApi(
    idToken: string,
    medicalUserId: string,
    patientId: string,
    onSuccess: GenericSuccessCallback,
    onError: ErrorWithMessageCallback,
    handleSessionExpiry?: () => void
) {
    try {
        const response = await fetch(`${BASE_URL}/api/medical-user/${medicalUserId}/remove/${patientId}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
            },
        });

        if (response.ok) {
            const result = await response.json();
            onSuccess(result);
            console.log("Patient successfully removed");
        } else if (response.status === 401) {
            // Handle session expiry
            if (handleSessionExpiry) {
                handleSessionExpiry();
            } else {
                console.error("Unauthorized - Session expired or invalid.");
            }
            onError("Session expired.");
        } else {
            onError("Failed to remove patient.");
        }
    } catch (error) {
        console.error("Error removing patient:", error);
        onError("An unexpected error occurred.");
    }
}

export async function assignMedicalUserToPatient(
    nutriId: string,
    userId: string,
    idToken: string,
): Promise<CalendlyExpert | null> {
    try {
        const response = await fetch(`${BASE_URL}/api/medical-user/assign/`, {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': idToken,
            },
            body: JSON.stringify(
                {
                    patient_id: userId,
                    medicaluser_id: nutriId,
                })
        })
        const expert = await response.json()
        if (expert) {
            return expert
        } else {
            throw new Error('No expert assigned');
        }
    } catch (error) {
        console.error("Assign nutritionist to patient id failed:", error);
        return null;
    }
}
